// The signed-in operator user's org (COL-353). Reads operator_members →
// operators for the current user; RLS lets a member see exactly their own org.
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export interface OperatorOrg {
  id: string;
  name: string;
  slug: string;
  is_platform: boolean;
  /** Reply-to + contact info on tenant mail; org super_admin edits it. */
  contact_email: string | null;
  member_role: "super_admin" | "admin";
}

export function useOperator(): {
  operator: OperatorOrg | null;
  isLoading: boolean;
} {
  const { user, hasOperatorRole, rolesReady } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["operator-org", user?.id],
    enabled: !!user && rolesReady && hasOperatorRole,
    // Org name/slug don't churn; a rename can wait for a reload.
    staleTime: Infinity,
    queryFn: async (): Promise<OperatorOrg | null> => {
      const { data: row, error } = await supabase
        .from("operator_members")
        .select(
          "member_role, operators(id, name, slug, is_platform, contact_email)"
        )
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!row?.operators) return null;
      const org = row.operators as Omit<OperatorOrg, "member_role">;
      return {
        ...org,
        member_role: row.member_role as OperatorOrg["member_role"],
      };
    },
  });

  return {
    operator: data ?? null,
    isLoading: hasOperatorRole ? isLoading : false,
  };
}
